import { useState } from "react";
import { Bug, X, Send, Camera, AlertCircle, Lightbulb, Gauge, Database, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";

const categoryIcons: Record<string, typeof Bug> = {
  bug: Bug,
  ui_issue: AlertCircle,
  feature_request: Lightbulb,
  data_error: Database,
  performance: Gauge,
  other: HelpCircle,
};

const categoryLabels: Record<string, string> = {
  bug: "Bug / Something Broken",
  ui_issue: "Display / UI Issue",
  feature_request: "Feature Request",
  data_error: "Wrong Data Showing",
  performance: "Slow / Performance",
  other: "Other",
};

export function ReportIssueButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<string>("bug");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const createIssue = trpc.issues.create.useMutation({
    onSuccess: () => {
      toast.success("Issue reported! We'll look into it.", { duration: 4000 });
      setOpen(false);
      setDescription("");
      setCategory("bug");
      setScreenshotFile(null);
    },
    onError: (err) => {
      toast.error("Failed to submit issue: " + err.message);
    },
  });

  if (!user) return null;

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please describe the issue");
      return;
    }

    setUploading(true);
    let screenshotUrl: string | undefined;

    // Upload screenshot if provided
    if (screenshotFile) {
      try {
        const formData = new FormData();
        formData.append("file", screenshotFile);
        const resp = await fetch("/api/upload", { method: "POST", body: formData });
        if (resp.ok) {
          const data = await resp.json();
          screenshotUrl = data.url;
        }
      } catch (e) {
        console.warn("Screenshot upload failed, continuing without it");
      }
    }

    createIssue.mutate({
      pageUrl: window.location.pathname,
      pageName: document.title || window.location.pathname.split("/").pop() || "Unknown Page",
      description: description.trim(),
      screenshotUrl,
      browserInfo: `${navigator.userAgent.slice(0, 200)}`,
      category: category as any,
    });
    setUploading(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 shadow-lg hover:shadow-xl transition-all duration-200 print:hidden group"
        title="Report an issue"
      >
        <Bug className="h-5 w-5" />
        <span className="text-sm font-medium hidden sm:inline group-hover:inline">Report Issue</span>
      </button>

      {/* Report Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-500" />
              Report an Issue
            </DialogTitle>
            <DialogDescription>
              Describe what's wrong or what you'd like changed. We'll review it and get it fixed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">What type of issue?</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(categoryLabels).map(([key, label]) => {
                  const Icon = categoryIcons[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setCategory(key)}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border text-left text-xs transition-all ${
                        category === key
                          ? "border-orange-500 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300"
                          : "border-border hover:border-muted-foreground/30 text-muted-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Describe the issue</Label>
              <Textarea
                placeholder="What's happening? What did you expect to happen? Be as specific as you can..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>

            {/* Screenshot Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1">
                <Camera className="h-3.5 w-3.5" />
                Screenshot (optional)
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setScreenshotFile(e.target.files?.[0] || null)}
                className="text-xs"
              />
              {screenshotFile && (
                <p className="text-xs text-muted-foreground">
                  {screenshotFile.name} ({(screenshotFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            {/* Context Info */}
            <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
              <p><strong>Page:</strong> {window.location.pathname}</p>
              <p><strong>Reported by:</strong> {user.name || user.email}</p>
              <p><strong>Time:</strong> {new Date().toLocaleString()}</p>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!description.trim() || createIssue.isPending || uploading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {createIssue.isPending || uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Submit Issue
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
